import type {
  AllAdjustmentsResponseDTO,
  AmountValueDTO,
  ForecastPreviewDataPointDTO,
  LoadPlanningCurveDataPointDTO,
  LoadPlanningResponseDto,
} from '@/src/api/types/procurement'
import type { DateRangePreset } from '@/src/components/DatePicker/datePicker.presets'
import type { DatePickerType } from '@/src/components/DatePicker/datePicker.types'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import type { ProcurementEnergyType } from '@/src/features/procurement-and-markets/load-planning/LoadPlanning.types'
import { appColors } from '@/src/theme'
import {
  addYears,
  endOfDay,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns'
import type { TFunction } from 'i18next'

const colorTable = {
  actualConsumptionValue: appColors.accentPrimary,
  savedValue: appColors.primary,
  draftValue: appColors.accentSecondary,
  previewValue: appColors.secondaryForeground,
}

export type LoadPlanningCurveDataPointValueProperties = Pick<
  LoadPlanningCurveDataPointDTO,
  'actualConsumptionValue' | 'draftValue' | 'savedValue'
>

export type LoadPlanningPreviewCurveDataPointValueProperties = Pick<ForecastPreviewDataPointDTO, 'draftValue' | 'previewValue'>

export const getColor = (
  dataKey: keyof LoadPlanningCurveDataPointValueProperties | keyof LoadPlanningPreviewCurveDataPointValueProperties
) => {
  return colorTable[dataKey]
}

export const isBeforeNowExclusive = (timestamp: string, granularity: GranularityEnum = GranularityEnum.ONE_DAY): boolean => {
  const date = new Date(timestamp)
  const now = new Date()

  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
      return startOfMinute(date) < startOfMinute(now)

    case GranularityEnum.ONE_HOUR:
      return startOfHour(date) < startOfHour(now)

    case GranularityEnum.ONE_DAY:
      return startOfDay(date) < startOfDay(now)

    case GranularityEnum.ONE_MONTH:
      return startOfMonth(date) < startOfMonth(now)

    default:
      return startOfDay(date) < startOfDay(now)
  }
}

export const extractValueForBar = (side: 'left' | 'right', point: LoadPlanningCurveDataPointDTO, granularity: GranularityEnum) => {
  const getter = side === 'left' ? getPropertyForLeftBar : getPropertyForRightBar
  const propertyName = getter(point, granularity)

  if (!propertyName) {
    return 0
  }

  const accessor: keyof AmountValueDTO = granularity === GranularityEnum.FIFTEEN_MINUTES ? 'power' : 'energy'
  return point[propertyName]?.[accessor]?.amount ?? 0
}

export const getPropertyForLeftBar = (
  point: LoadPlanningCurveDataPointDTO,
  granularity: GranularityEnum
): keyof LoadPlanningCurveDataPointValueProperties => {
  if (isBeforeNowExclusive(point.timestampStart, granularity)) return 'actualConsumptionValue'
  return 'savedValue'
}
export const getPropertyForRightBar = (
  point: LoadPlanningCurveDataPointDTO,
  granularity: GranularityEnum
): keyof LoadPlanningCurveDataPointValueProperties => {
  if (isBeforeNowExclusive(point.timestampStart, granularity)) return 'savedValue'
  return 'draftValue'
}

interface SimpleAdjustment {
  id: string | number
  locationId?: number
  name: string
}
type SimpleAdjustmentMap = Map<string | number, SimpleAdjustment>

export const getAdjustmentMap = (adjustments?: AllAdjustmentsResponseDTO): SimpleAdjustmentMap => {
  if (!adjustments) return new Map()
  const flatAdjustments = [
    ...adjustments.absoluteQuantity,
    ...adjustments.companyHolidays,
    ...adjustments.flexibility,
    ...adjustments.measureSimulationCandidates,
    ...adjustments.percentageQuantity,
    ...adjustments.renewableProduction,
  ]

  return new Map(flatAdjustments.map(a => [a.id, a]))
}

export const getDateRangeOfYear = (yearInput?: number | string) => {
  if (!yearInput) {
    return { minDate: undefined, maxDate: undefined }
  }
  const year = Number(yearInput)
  return {
    minDate: new Date(year, 0, 1), // January 1st of strategy year
    maxDate: new Date(year, 11, 31), // December 31st of strategy year
  }
}

/**
 * Filters data points to only include those within the specified date range.
 * @param points - Array of normalized forecast data points
 * @param dateRange - Optional date range with from and to dates
 * @returns Filtered array of data points within the date range
 */
export const zoomDataPointsTimeRange = <T extends { timestampStart: string }>(points: T[], dateRange?: { from: Date; to: Date }): T[] => {
  if (!dateRange) return points

  const fromTime = dateRange.from.getTime()
  const toTime = dateRange.to.getTime()

  return points.filter(point => {
    if (!point.timestampStart) return false
    const pointTime = new Date(point.timestampStart).getTime()
    return pointTime >= fromTime && pointTime <= toTime
  })
}

export const getDataByGranularity = (
  loadForecastData: LoadPlanningResponseDto,
  granularity: GranularityEnum
): LoadPlanningCurveDataPointDTO[] => {
  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
      return loadForecastData.quarterHourlyData ?? []
    case GranularityEnum.ONE_HOUR:
      return loadForecastData.hourlyData ?? []
    case GranularityEnum.ONE_MONTH:
      return loadForecastData.monthlyData ?? []
    default:
      return loadForecastData.monthlyData ?? []
  }
}

export const getPeakPointValue = (dataSource: LoadPlanningCurveDataPointDTO[], granularity: GranularityEnum): number => {
  const unitProperty = getEnergyUnitType(granularity)
  return dataSource.reduce((prev, tick) => {
    const draftAmount = tick.draftValue?.[unitProperty]?.amount ?? 0
    const savedAmount = tick.savedValue?.[unitProperty]?.amount ?? 0
    const actualAmount = tick.actualConsumptionValue?.[unitProperty]?.amount ?? 0

    if (draftAmount > prev) return draftAmount
    if (savedAmount > prev) return savedAmount
    if (actualAmount > prev) return actualAmount
    return prev
  }, 0)
}

export const getXAxisInterval = (granularity: GranularityEnum, dataLength: number): number =>
  granularity === GranularityEnum.ONE_MONTH ? 0 : dataLength / 12

export const getDateFormatByGranularity = (granularity: GranularityEnum): string => {
  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
    case GranularityEnum.ONE_HOUR:
      return 'dd MMM'
    case GranularityEnum.ONE_DAY:
      return 'dd MMM'
    case GranularityEnum.ONE_MONTH:
      return 'MMM yyyy'

    default:
      return 'dd.MM.yyyy'
  }
}

export const getDatePickerTypeFromGranularity = (granularity: GranularityEnum): DatePickerType => {
  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
    case GranularityEnum.ONE_HOUR:
      return 'day'
    case GranularityEnum.ONE_DAY:
      return 'day'
    case GranularityEnum.ONE_MONTH:
      return 'month'
    default:
      return 'day'
  }
}

export const getLoadPlanningEnergyTypeLabel = (t: TFunction, energyType?: ProcurementEnergyType) => {
  switch (energyType) {
    case 'ELECTRICITY':
      return t('components.procurement.energy_type.electricity')
    case 'GAS':
      return t('components.procurement.energy_type.gas')
    default:
      return t('components.procurement.energy_type.electricity')
  }
}

export const generateDatePickerPreset = (year: number): DateRangePreset[] => {
  return [
    {
      value: 'entire-year',
      getDateRange: () => {
        const date = new Date(year, 1, 1)
        return [startOfYear(date), endOfYear(endOfDay(date))]
      },
    },
    {
      value: 'q1',
      getDateRange: () => {
        const date = new Date(year, 1, 1)
        return [startOfQuarter(date), endOfQuarter(date)]
      },
    },
    {
      value: 'q2',
      getDateRange: () => {
        const date = new Date(year, 4, 1)
        return [startOfQuarter(date), endOfQuarter(date)]
      },
    },
    {
      value: 'q3',
      getDateRange: () => {
        const date = new Date(year, 7, 1)
        return [startOfQuarter(date), endOfQuarter(date)]
      },
    },
    {
      value: 'q4',
      getDateRange: () => {
        const date = new Date(year, 10, 1)
        return [startOfQuarter(date), endOfQuarter(date)]
      },
    },
  ]
}

export const getEnergyUnitType = (granularity: GranularityEnum): keyof AmountValueDTO => {
  switch (granularity) {
    case GranularityEnum.ONE_MINUTE:
    case GranularityEnum.FIVE_MINUTES:
    case GranularityEnum.FIFTEEN_MINUTES:
    case GranularityEnum.THIRTY_MINUTES:
    case GranularityEnum.ONE_HOUR:
      return 'power'
    default:
      return 'energy'
  }
}

export const mapLoadPlanningUnitText = (input?: string) => {
  switch (input) {
    case 'KWh':
      return 'kWh'
    case 'KW':
      return 'kW'
    default:
      return input
  }
}

export const getLoadPlanningLegend = (curve: LoadPlanningCurveDataPointDTO[], t: TFunction) => {
  const legendMap = {
    actualConsumptionValue: curve.some(c => !!c.actualConsumptionValue),
    draftValue: curve.some(c => !!c.draftValue),
    savedValue: curve.some(c => !!c.savedValue),
  }
  const legend: { text: string; lineColor: string; iconType: 'box' }[] = []

  if (legendMap.actualConsumptionValue) {
    legend.push({
      text: t('components.procurement.forecasting.graph.legend_actual_consumption'),
      lineColor: getColor('actualConsumptionValue'),
      iconType: 'box',
    })
  }

  if (legendMap.savedValue) {
    legend.push({
      text: t('components.procurement.forecasting.graph.legend_saved_forecast'),
      lineColor: getColor('savedValue'),
      iconType: 'box',
    })
  }

  if (legendMap.draftValue) {
    legend.push({
      text: t('components.procurement.forecasting.graph.legend_draft_forecast'),
      lineColor: getColor('draftValue'),
      iconType: 'box',
    })
  }
  return legend
}

export const getLastYearsWeekRange = () => {
  const today = new Date()
  const previousYear = addYears(today, -1)
  return [startOfWeek(previousYear), endOfWeek(previousYear)]
}

export const getGranularityOptions = (energyType: 'ELECTRICITY' | 'GAS', t: TFunction) => {
  if (energyType === 'GAS') {
    return [
      { value: GranularityEnum.ONE_HOUR, label: t('load_profile') },
      { value: GranularityEnum.ONE_MONTH, label: t('reports.modules.granularities.monthly') },
    ]
  }
  return [
    { value: GranularityEnum.FIFTEEN_MINUTES, label: t('load_profile') },
    { value: GranularityEnum.ONE_MONTH, label: t('reports.modules.granularities.monthly') },
  ]
}

export const isLoadPlanningDraft = (loadPlanning: LoadPlanningResponseDto) => {
  return loadPlanning.monthlyData.some(c => !!c.draftValue)
}

export const countAdjustments = (adjustments: AllAdjustmentsResponseDTO) => {
  return (
    adjustments.companyHolidays.length +
    adjustments.absoluteQuantity.length +
    adjustments.percentageQuantity.length +
    adjustments.flexibility.length +
    adjustments.renewableProduction.length +
    adjustments.referenceBaseloadCorrection.length +
    adjustments.measureSimulationCandidates.length
  )
}
